import Swiper from "swiper";
import { Navigation } from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import { onWindowResize, removeCallback } from "~/foundation/Events/onWindowResize";
import { updateDetailYearDotsActive } from "./detail-dot-animations";
import {
	DETAIL_EVENT_SLIDE_MOBILE_SLIDES_PER_VIEW,
	DETAIL_EVENT_SLIDE_SPACE_BETWEEN_PX,
	DETAIL_YEAR_SWIPER_SPEED_MS,
	TIMELINE_DESKTOP_MIN_WIDTH_PX,
} from "./timeline-constants";

export type DetailYearRowElement = HTMLElement & {
	_yearSwiper?: Swiper;
	_swiperCleanup?: () => void;
	_activeDotIndex?: number;
};

export { updateDetailYearDotsActive } from "./detail-dot-animations";

function isDesktopDetailEventSwiperViewport(): boolean {
	return window.innerWidth >= TIMELINE_DESKTOP_MIN_WIDTH_PX;
}

function getDetailEventSlidesPerView(hasMultipleSlides: boolean): number {
	if (!hasMultipleSlides) {
		return 1;
	}

	return isDesktopDetailEventSwiperViewport()
		? 1
		: DETAIL_EVENT_SLIDE_MOBILE_SLIDES_PER_VIEW;
}

export function destroyDetailYearSwiper(row: DetailYearRowElement): void {
	row._swiperCleanup?.();
	row._swiperCleanup = undefined;
	row._yearSwiper = undefined;
	row._activeDotIndex = undefined;
}

export function initDetailYearSwiper(
	row: DetailYearRowElement,
	options: {
		initialIndex: number;
		onSlideChange: (index: number) => void;
	}
): Swiper | undefined {
	destroyDetailYearSwiper(row);

	const swiperEl = row.querySelector<HTMLElement>(".historical-timeline__detail-event-swiper");
	if (!swiperEl) {
		return undefined;
	}

	swiperEl.querySelectorAll<HTMLElement>(".historical-timeline__detail-event-slide").forEach((slide) => {
		slide.hidden = false;
	});

	if (document.documentElement.dir === "rtl") {
		swiperEl.setAttribute("dir", "rtl");
	}

	const prevButton = row.querySelector<HTMLElement>(".swiper-button-prev");
	const nextButton = row.querySelector<HTMLElement>(".swiper-button-next");
	const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
	if (slideCount <= 1) {
		return undefined;
	}

	const swiperConfig: SwiperOptions = {
		modules: [Navigation],
		slidesPerView: getDetailEventSlidesPerView(true),
		spaceBetween: DETAIL_EVENT_SLIDE_SPACE_BETWEEN_PX,
		initialSlide: options.initialIndex,
		loop: true,
		speed: DETAIL_YEAR_SWIPER_SPEED_MS,
		autoHeight: true,
		allowTouchMove: true,
		watchOverflow: false,
		runCallbacksOnInit: false,
		navigation:
			prevButton && nextButton
				? {
					prevEl: prevButton,
					nextEl: nextButton,
					// Custom CSS icons via span; prevent Swiper v12+ default SVG injection
					addIcons: false,
				}
				: false,
		on: {
			slideChange: (swiper) => {
				const realIndex = swiper.realIndex;
				updateDetailYearDotsActive(row, realIndex);
				options.onSlideChange(realIndex);
			},
		},
	};

	const swiper = new Swiper(swiperEl, swiperConfig);

	void document.fonts?.ready?.then(() => {
		if (!swiper.destroyed) {
			swiper.update();
		}
	});

	if (swiper.params.loop && swiper.realIndex !== options.initialIndex) {
		swiper.slideToLoop(options.initialIndex, 0);
	}

	updateDetailYearDotsActive(row, swiper.realIndex);
	options.onSlideChange(swiper.realIndex);
	row._yearSwiper = swiper;

	const syncSlidesPerView = () => {
		if (swiper.destroyed) {
			return;
		}

		const nextSlidesPerView = getDetailEventSlidesPerView(true);
		if (swiper.params.slidesPerView === nextSlidesPerView) {
			return;
		}

		swiper.params.slidesPerView = nextSlidesPerView;
		swiper.update();
	};

	onWindowResize(syncSlidesPerView);

	row._swiperCleanup = () => {
		removeCallback(syncSlidesPerView);
		if (!swiper.destroyed) {
			swiper.destroy(true, true);
		}
	};

	return swiper;
}

export function slideDetailYearTo(row: DetailYearRowElement, index: number): void {
	const swiper = row._yearSwiper;
	if (!swiper || swiper.destroyed) {
		return;
	}

	if (swiper.realIndex === index) {
		updateDetailYearDotsActive(row, index);
		return;
	}

	if (swiper.params.loop) {
		swiper.slideToLoop(index);
		return;
	}

	swiper.slideTo(index);
}
